import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { collection, getDocs, query, where } from 'firebase/firestore';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  CircularProgress,
  Paper,
  Divider,
} from '@mui/material';
import {
  School as SchoolIcon,
  Person as PersonIcon,
  SupervisorAccount as AdminIcon,
  Engineering as ServiceIcon,
} from '@mui/icons-material';

const StatCard = ({ title, value, icon, color }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography color="textSecondary" gutterBottom variant="h6">
            {title}
          </Typography>
          <Typography variant="h4">
            {value}
          </Typography>
        </Box>
        <Box 
          sx={{ 
            backgroundColor: `${color}20`,
            borderRadius: '50%',
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const SuperAdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalEstablishments: 0,
    totalUsers: 0,
    totalAdmins: 0,
    totalChefServices: 0,
    usersByRole: {},
    usersByEstablishment: {},
  });

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      // Récupérer les établissements
      const establishmentsSnapshot = await getDocs(collection(db, 'establishments'));
      const totalEstablishments = establishmentsSnapshot.size;

      // Récupérer tous les utilisateurs
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const users = usersSnapshot.docs.map(doc => doc.data());

      // Calculer les statistiques
      const usersByRole = users.reduce((acc, user) => {
        acc[user.role] = (acc[user.role] || 0) + 1;
        return acc;
      }, {});

      // Calculer les utilisateurs par établissement
      const usersByEstablishment = users.reduce((acc, user) => {
        if (user.establishmentId) {
          acc[user.establishmentId] = (acc[user.establishmentId] || 0) + 1;
        }
        return acc;
      }, {});

      setStats({
        totalEstablishments,
        totalUsers: users.length,
        totalAdmins: usersByRole['admin'] || 0,
        totalChefServices: usersByRole['chefService'] || 0,
        usersByRole,
        usersByEstablishment,
      });

      setLoading(false);
    } catch (error) {
      console.error("Erreur lors de la récupération des statistiques:", error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" sx={{ mb: 4 }}>
        Tableau de Bord Super Admin
      </Typography>

      <Grid container spacing={3}>
        {/* Cartes de statistiques principales */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Établissements"
            value={stats.totalEstablishments}
            icon={<SchoolIcon sx={{ color: '#1976d2', fontSize: 40 }} />}
            color="#1976d2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Utilisateurs"
            value={stats.totalUsers}
            icon={<PersonIcon sx={{ color: '#2e7d32', fontSize: 40 }} />}
            color="#2e7d32"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Administrateurs"
            value={stats.totalAdmins}
            icon={<AdminIcon sx={{ color: '#ed6c02', fontSize: 40 }} />}
            color="#ed6c02"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Chefs de Service"
            value={stats.totalChefServices}
            icon={<ServiceIcon sx={{ color: '#9c27b0', fontSize: 40 }} />}
            color="#9c27b0"
          />
        </Grid>

        {/* Statistiques détaillées */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Répartition des Utilisateurs par Rôle
            </Typography>
            <Box sx={{ mt: 2 }}>
              {Object.entries(stats.usersByRole).map(([role, count]) => (
                <Box key={role} sx={{ mb: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body1">
                      {role.charAt(0).toUpperCase() + role.slice(1)}
                    </Typography>
                    <Typography variant="body1">
                      {count} utilisateur{count > 1 ? 's' : ''}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: '100%',
                      height: 8,
                      bgcolor: '#f5f5f5',
                      mt: 1,
                      borderRadius: 1,
                      overflow: 'hidden'
                    }}
                  >
                    <Box
                      sx={{
                        width: `${(count / stats.totalUsers) * 100}%`,
                        height: '100%',
                        bgcolor: role === 'superAdmin' ? '#1976d2' :
                                role === 'admin' ? '#ed6c02' :
                                role === 'chefService' ? '#9c27b0' :
                                '#2e7d32'
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default SuperAdminDashboard;
